import { CrossCheckFinding } from '../validation/crossCheckEngine';

/**
 * Filtre d'AFFICHAGE des findings, partagé entre /form-cgia (colonne Alertes
 * agence) et /cg/:id/review (colonne Alertes god). C'est la garantie d'iso :
 * les deux vues partent de l'union brute des moteurs (`mergedFindings` côté
 * form, `snapshot.findings` rejoué côté review) et appliquent EXACTEMENT le
 * même filtre avant de compter / afficher.
 *
 * Sans ce module partagé, la review affichait un compteur "blocages" dérivé
 * par PIÈCE (un finding multi-pièces comptait 2×) là où form-cgia compte par
 * FINDING — d'où "3 blocages" en review vs "2 majeures" dans le form pour le
 * même dossier (EL-659-GT : `date_cerfa_coherente` touche cession + 13750).
 */

/**
 * RuleId volontairement NON affichés dans la colonne Alertes (anxiogènes /
 * redondants). La règle continue de tourner (trace console + snapshot + usage
 * GOD / audit), seul l'affichage l'occulte.
 *  - `coherence_immat_skipped_low_confidence` : double
 *    `misiv_skipped_low_confidence` (qui dit déjà « confirmer la plaque »).
 *  - `coherence_vin_cg_cession` : retirée de l'affichage form-cgia ET review
 *    (demande Luc 2026-07-29). Le filtrage couvre aussi les snapshots des
 *    dossiers déjà soumis (rejoués verbatim côté review).
 */
export const HIDDEN_FROM_AGENCY_RULE_IDS = new Set<string>([
  'coherence_immat_skipped_low_confidence',
  'coherence_vin_cg_cession',
]);

/**
 * RuleId réservés à la vue DIEU : visibles pour le god (quelle que soit leur
 * sévérité), masqués pour l'agence — à la différence de `HIDDEN_FROM_AGENCY_RULE_IDS`
 * qui masque des DEUX vues. Sert aux signaux internes que l'agence ne doit pas voir :
 *  - `incoherence_document` : « suspicion de faux » (MINEURE) — anxiogène / à arbitrer
 *    par le pôle god, jamais montré à l'agence.
 *  - `ct_valide_via_cg_x1` : règle retirée le 2026-09-24, mais encore présente dans
 *    les analyses déjà enregistrées : on la garde masquée à l'agence.
 */
export const GOD_ONLY_RULE_IDS = new Set<string>(['incoherence_document', 'ct_valide_via_cg_x1']);

/**
 * Applique le filtre d'affichage.
 *  - on retire toujours les ruleId masqués des deux vues (`HIDDEN_FROM_AGENCY_RULE_IDS`) ;
 *  - en vue AGENCE (`!isGod`) : on retire aussi les INFO (diagnostic interne) ET les
 *    `GOD_ONLY_RULE_IDS` (ex. suspicion de faux) ;
 *  - en vue DIEU (`isGod`) : tout est conservé (INFO + god-only inclus).
 *
 * La review est god-only → `isGod: true` y conserve tout, exactement comme
 * form-cgia quand un god teste un dossier.
 */
export const filterFindingsForDisplay = (
  findings: CrossCheckFinding[],
  { isGod }: { isGod: boolean },
): CrossCheckFinding[] =>
  findings.filter(
    (f) =>
      !HIDDEN_FROM_AGENCY_RULE_IDS.has(f.ruleId) &&
      (isGod || (f.severity !== 'info' && !GOD_ONLY_RULE_IDS.has(f.ruleId))),
  );
